// React
import { Fragment } from "react"

// Carousel
import "react-multi-carousel/lib/styles.css"

// Components
import { Button, HeaderMenu, Loading, Modal, PermissionLink } from "components"
import {
  CardSection,
  StockAnalysis
} from "pages/Inventori/Transaction/AddNewItem/components/ChildItem/components/Item/components"
import { Chart } from "pages/Inventori/Transaction/AddNewItem/components/ChildItem/components/Item/components/DataLocationForm"

// Utils
import { convertNumber, useToggle } from "utils"
import { usePerformence } from "pages/Inventori/Transaction/AddNewItem/components/FormSection/utils"
import {
  useDefaultSize,
  useDetailPersonalData,
  useProductLocation
} from "pages/Inventori/Transaction/AddNewItem/components/ChildItem/components/Item/utils"

export function InventoryDetailModal(params: {
  barcode: string
  id: string
}): JSX.Element {
  // Hooks
  const { isActive, toggle } = useToggle(false)

  return (
    <Fragment>
      <div className="cursor-pointer" onClick={toggle}>
        {params.barcode}
      </div>

      {isActive && (
        <Modal isOpen closeModal={toggle} title="DETAIL PRODUCT">
          <Modal.Body>
            <Header id={params.id} />
            <Stock id={params.id} />
          </Modal.Body>
        </Modal>
      )}
    </Fragment>
  )
}

function Header(params: { id: string }): JSX.Element {
  // Hooks
  const product = useDetailPersonalData(parseInt(params.id))
  const { dataDefault } = useDefaultSize()

  return (
    <HeaderMenu title="ITEM PROFILE">
      {dataDefault && product.dataDetail && (
        <PermissionLink
          permission="IR012"
          to={`/inventory/transaction/add-new-item/update/${product.dataDetail.product_id}`}>
          <Button color="primary">EDIT ITEM</Button>
        </PermissionLink>
      )}
    </HeaderMenu>
  )
}

function Stock(params: { id: string }): JSX.Element {
  // Hooks
  const { data, isLoading } = usePerformence({
    product_id: parseInt(params.id)
  })

  if (isLoading || !data) {
    return <Loading loading={isLoading} />
  }

  const cogm_current_value = data?.cogm_current_value ? convertNumber(Math.round(data?.cogm_current_value))?.intoNormalAmount! : "0"
//   const average_current = data?.average_current ? convertNumber(Math.round(data?.average_current))?.intoNormalAmount! : "0"

  return (
    <section className="flex flex-col gap-3">
      <section className="flex gap-2">
        <section>
          <div className="text-[16px] mb-5">
            <b>STOCK ANALYSIS</b>
          </div>
        <StockAnalysis
            label="Stock In"
            value={
            data?.stock_in
                ? convertNumber(data?.stock_in)?.intoNormalAmount!
                : "0"
            }
        />
        <StockAnalysis
            label="Stock Out"
            value={
            data?.stock_out
                ? convertNumber(data?.stock_out)?.intoNormalAmount!
                : "0"
            }
            textError
        />
        <StockAnalysis
            label="Quantity On Hand :"
            value={
            data?.stock_on_hand
                ? convertNumber(data?.stock_on_hand)?.intoNormalAmount!
                : "0"
            }
        />
        <StockAnalysis 
                label="COGM Current Value :" value={cogm_current_value} 
                hint={`${ data?.stock_on_hand ?? 0} x ${ data?.cogm_product ? convertNumber(data?.cogm_product).intoNormalAmount! : 0} = ${cogm_current_value}`}
            />

        <StockAnalysis
            label="Average Cost :"
            value={
            data?.average_cost
                ? convertNumber(data?.average_cost)?.intoNormalAmount!
                : "0"
            }
        />
        <StockAnalysis
            label="Committed :"
            value={
            data?.commited
                ? convertNumber(data?.commited)?.intoNormalAmount!
                : "0"
            }
        />
        <StockAnalysis
            label="On Vendor Order :"
            value={
            data?.on_vendor
                ? convertNumber(data?.on_vendor)?.intoNormalAmount!
                : "0"
            }
        />
        <StockAnalysis
            label="Available :"
            value={
            data?.available
                ? convertNumber(data?.available)?.intoNormalAmount!
                : "0"
            }
        />
        <StockAnalysis 
            label="Retail Current Value :" 
            value={data?.retail_current_value ? convertNumber(data?.retail_current_value)?.intoNormalAmount! : "0"} 
            hint={`${ data?.stock_on_hand ?? 0} x ${ convertNumber(data?.selling_retail_price ?? 0).intoCurrency } = ${convertNumber(data?.retail_current_value??0)?.intoNormalAmount!}`}
        />
        </section>
        
      </section>

      <section className="flex gap-3">
        <section className="grow overflow-x-auto flex flex-col gap-3">
          <section className="flex gap-2">
            <CardSection 
                className="!bg-[#758694] !border-[#758694]" 
                label="Inventory Adjustment" 
                value={data?.resume_inventory_adjustment ? convertNumber(data?.resume_inventory_adjustment)?.intoNormalAmount! : "0"} 
                text={data?.resume_inventory_adjustment_unit ?? "-"}
            />
            <CardSection 
                className="!bg-[#FFAF00] !border-[#FFAF00]" 
                label="On Order" 
                value={data?.resume_on_order ? convertNumber(data?.resume_on_order)?.intoNormalAmount! : "0"} 
                text={data?.resume_on_order_unit ?? "-"}
            />
            <CardSection 
                className="!bg-[#387F39] !border-[#387F39]" 
                label="Delivered" 
                value={data?.resume_delivered ? convertNumber(data?.resume_delivered)?.intoNormalAmount! : "0"} 
                text={data?.resume_delivered_unit ?? "-"}
            />
            <CardSection 
                className="!bg-[#5B99C2] !border-[#5B99C2]" 
                label="Commited" 
                value={data?.resume_commited ? convertNumber(data?.resume_commited)?.intoNormalAmount! : "0"} 
                text={data?.resume_commited_unit ?? "-"}
            />
            <CardSection 
                className="!bg-[#03346E] !border-[#03346E]" 
                label="Available" 
                value={data?.available ? convertNumber(data?.available)?.intoNormalAmount! : "0"} 
                text={data?.resume_sold_unit ?? "-"}
            />
          </section>

          <section className="flex gap-2">
            <CardSection 
                className="!bg-[#399918] !border-[#399918]" 
                label="On Customer Quotation" 
                value={data?.resume_customer_quotation ? convertNumber(data?.resume_customer_quotation)?.intoNormalAmount! : "0"} 
                text={data?.resume_customer_quotation_unit ?? "-"}
            />
            <CardSection 
                className="!bg-[#FD9B63] !border-[#FD9B63]" 
                label="Build Component" 
                value={data?.resume_build_component ? convertNumber(data?.resume_build_component)?.intoNormalAmount! : "0"} 
                text={data?.resume_build_component_unit ?? "-"}
            />
            <CardSection 
                className="!bg-[#1A5319] !border-[#1A5319]" 
                label="Total Stock Incoming" 
                value={data?.resume_stock_in ? convertNumber(data?.resume_stock_in)?.intoNormalAmount! : "0"} 
                text={data?.resume_stock_in_unit ?? "-"}
            />
            <CardSection 
                className="!bg-[#A02334] !border-[#A02334]" 
                label="Total Stock Out Going" 
                value={data?.resume_stock_out ? convertNumber(data?.resume_stock_out)?.intoNormalAmount! : "0"} 
                text={data?.resume_stock_out_unit ?? "-"}
            />
            <CardSection 
                className="!bg-[#658147] !border-[#658147]" 
                label="Total Stock On Hand" 
                value={data?.resume_stock_on_hand ? convertNumber(data?.resume_stock_on_hand)?.intoNormalAmount! : "0"} 
                text={data?.resume_stock_on_hand_unit ?? "-"}
            />
          </section>
        </section>
      </section>

      <ChartSection id={params.id} />
    </section>
  )
}

function ChartSection(params: { id: string }): JSX.Element {
  // Hooks
  const { data, isLoading } = useProductLocation(parseInt(params.id))

  if (isLoading || !data) {
    return <Loading loading={isLoading} />
  }

  return <Chart data={data} />
}
